import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  cancelRegistrationByToken,
  getEventById,
  resetEvent,
} from "../../store/events";
import useNoIndex from "../../hooks/useNoIndex";
import ContactCard from "./ContactCard";
import EventShell from "./EventShell";
import { Button } from "./FormControls";
import Icon from "./Icon";
import { formatEventDate, formatWindow, shopTitle } from "./nsdFields";
import "./events.css";

const Cancelled = ({ event, eventId }) => (
  <div className="ev-wrap ev-wrap--narrow ev-confirm-center">
    <div className="ev-confirm-badge">
      <Icon
        name="check"
        size={38}
        color="var(--brand-legacy-green)"
        stroke={2.5}
      />
    </div>
    <h1 className="ev-h1">Your spot has been cancelled</h1>
    <p className="ev-lead ev-lead--center">
      Thanks for letting us know. Your time is now free for another guest.
    </p>
    {event?.pageMode === "classic" && (
      <p className="ev-lead ev-lead--center ev-follow-up">
        Changed your mind? <Link to={`/events/${eventId}`}>Book again</Link>.
      </p>
    )}
  </div>
);

const InvalidToken = ({ shop }) => (
  <div className="ev-wrap ev-wrap--narrow ev-confirm-center">
    <div className="ev-alert-badge">
      <Icon name="warn" size={34} color="var(--brand-persimmon)" />
    </div>
    <h1 className="ev-h1">This cancellation link isn't valid</h1>
    <p className="ev-lead ev-lead--center">
      The link may be incomplete or expired. Please use the link in your most
      recent confirmation email, or contact the shop and we'll cancel it for
      you.
    </p>
    <ContactCard shop={shop} />
  </div>
);

const CutoffPassed = ({ shop }) => (
  <div className="ev-wrap ev-wrap--narrow ev-confirm-center">
    <div className="ev-alert-badge">
      <Icon name="warn" size={34} color="var(--brand-persimmon)" />
    </div>
    <h1 className="ev-h1">It's too late to cancel online</h1>
    <p className="ev-lead ev-lead--center">
      We're too close to the event to cancel from here. Give the shop a call and
      we'll take care of it.
    </p>
    <ContactCard shop={shop} label="Call us at" />
  </div>
);

/**
 * Landing page for the cancel link in a guest's confirmation email.
 *
 * The cancel is only ever sent on an explicit click. Mail clients and security
 * gateways follow links in messages, so cancelling on mount would drop bookings
 * for guests who never opened the page.
 */
const CancelPage = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const dispatch = useDispatch();
  const { event, status, cancelStatus, cancelError } = useSelector(
    (state) => state.events
  );

  useNoIndex();

  // Resolved only for context — which event, and who to call if it's too late.
  useEffect(() => {
    dispatch(resetEvent());
    dispatch(getEventById(eventId));
  }, [eventId, dispatch]);

  const shop = event?.shop;
  const body = () => {
    if (cancelStatus === "done") {
      return <Cancelled event={event} eventId={eventId} />;
    }
    if (cancelError === "invalid_token") {
      return <InvalidToken shop={shop} />;
    }
    if (cancelError === "cutoff_passed") {
      return <CutoffPassed shop={shop} />;
    }

    return (
      <div className="ev-wrap ev-wrap--narrow ev-confirm-center">
        <h1 className="ev-h1">Cancel your spot?</h1>
        <p className="ev-lead ev-lead--center">
          {status === "ready"
            ? "This will cancel your registration for the event below."
            : "This will cancel your registration."}
        </p>

        {status === "ready" && event && (
          <div className="ev-confirm-card">
            <div className="ev-confirm-card-title">{event.name}</div>
            <div className="ev-confirm-line">
              <Icon name="calendar" color="var(--brand-legacy-green)" />{" "}
              {formatEventDate(event.eventDate)}
            </div>
            <div className="ev-confirm-line">
              <Icon name="clock" color="var(--brand-legacy-green)" />{" "}
              {formatWindow(event.startTime, event.endTime)}
            </div>
            <div className="ev-confirm-line">
              <Icon name="pin" color="var(--brand-legacy-green)" />{" "}
              {event.location || shopTitle(event.shop?.name)}
            </div>
          </div>
        )}

        {cancelError === "failed" && (
          <div className="ev-conflict" style={{ marginTop: 20 }}>
            Something went wrong cancelling your spot. Please try again, or call
            the shop and we'll take care of it.
          </div>
        )}

        <div className="ev-actions ev-actions--center">
          <Button
            kind="primary"
            onClick={() => dispatch(cancelRegistrationByToken(token))}
            disabled={cancelStatus === "submitting"}
            busy={cancelStatus === "submitting"}
          >
            {cancelStatus === "submitting"
              ? "Cancelling…"
              : "Yes, cancel my spot"}
          </Button>
          <Link className="ev-btn ev-btn--secondary" to={`/events/${eventId}`}>
            Keep my spot
          </Link>
        </div>
      </div>
    );
  };

  return <EventShell>{body()}</EventShell>;
};

export default CancelPage;
